import * as React from "react"
import * as RadioGroupPrimitive from "@radix-ui/react-radio-group"
import { Circle } from "lucide-react"

import { cn } from "../../lib/utils"
import type { RadioGroupItemProps } from "./Radio.types"

const RadioGroup = React.forwardRef<
  React.ElementRef<typeof RadioGroupPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Root>
>(({ className, ...props }, ref) => {
  return (
    <RadioGroupPrimitive.Root
      className={cn("grid gap-2", className)}
      {...props}
      ref={ref}
    />
  )
})
RadioGroup.displayName = RadioGroupPrimitive.Root.displayName

const RadioGroupItem = React.forwardRef<
  React.ElementRef<typeof RadioGroupPrimitive.Item>,
  RadioGroupItemProps
>(({ className, size = 'md', labelPosition = 'end', invalid = false, label, description, id: providedId, ...props }, ref) => {
  const fallbackId = React.useId();
  const id = providedId ?? fallbackId;

  const sizeClasses = {
    sm: "h-4 w-4",
    md: "h-5 w-5",
    lg: "h-6 w-6",
  };

  const iconSizes = {
    sm: "h-2 w-2",
    md: "h-2 w-2",
    lg: "h-3 w-3",
  };

  const labelSizes = {
    sm: "text-sm",
    md: "text-base",
    lg: "text-lg",
  };

  const radioItem = (
    <RadioGroupPrimitive.Item
      ref={ref}
      id={id}
      className={cn(
        "aspect-square rounded-full border ring-offset-background", 
        "focus:outline-hidden focus-visible:ring-4 focus-visible:ring-offset-2",
        "shrink-0 transition-all duration-150 disabled:cursor-not-allowed disabled:opacity-50",
        invalid
          ? "border-destructive data-[state=checked]:bg-primary data-[state=checked]:dark:bg-primary-300 data-[state=checked]:border-destructive text-destructive focus-visible:ring-red-400 focus-visible:ring-offset-red-100 dark:focus-visible:ring-red-500/50 dark:focus-visible:ring-offset-gray-900"
          : "border-zinc-200 data-[state=checked]:bg-primary data-[state=checked]:dark:bg-primary-200 data-[state=checked]:border-primary-500 data-[state=checked]:dark:border-primary-300 text-primary focus-visible:ring-primary-400 focus-visible:ring-offset-primary-100 dark:focus-visible:ring-primary-500/50 dark:focus-visible:ring-offset-gray-900",
        sizeClasses[size]
      )}
      {...props}
    >
      <RadioGroupPrimitive.Indicator className="flex items-center justify-center h-full w-full">
        <Circle className={cn("fill-white text-white dark:fill-primary dark:text-primary relative max-w-full max-h-full", iconSizes[size])} />
      </RadioGroupPrimitive.Indicator>
    </RadioGroupPrimitive.Item>
  );

  if (!label && !description) {
    return radioItem;
  }

  return (
    <div className={cn(
      "flex items-start gap-3",
      labelPosition === 'start' ? "flex-row-reverse justify-end w-full" : "flex-row",
      className
    )}>
      <div className={cn("flex pt-0.5", labelPosition === 'start' ? "ml-auto" : "")}>
        {radioItem}
      </div>
      <div className="flex flex-col gap-1 flex-1">
        {label && (
          <label
            htmlFor={id}
            className={cn(
              "font-medium peer-disabled:cursor-not-allowed peer-disabled:opacity-70 leading-none cursor-pointer",
              invalid && "text-destructive",
              labelSizes[size]
            )}
          >
            {label}
          </label>
        )}
        {description && (
          <p className={cn("text-sm peer-disabled:opacity-70", invalid ? "text-destructive" : "text-muted-foreground")}>
            {description}
          </p>
        )}
      </div>
    </div>
  );
})
RadioGroupItem.displayName = RadioGroupPrimitive.Item.displayName

// Mantenemos la exportacion Radio por si se importaba asi externamente
export { RadioGroup, RadioGroupItem, RadioGroupItem as Radio }
